/**
 * Auth Page - Combined Login and Registration
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import { FormInput } from '../components/FormInput';
import BranchSelector from '../components/BranchSelector';
import { useFormValidation } from '../hooks/useFormValidation';
import PasswordResetModal from '../components/PasswordResetModal';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
// import { tryoutApi } from '../services/tryoutApi'; // DISABLED
import { AlertTriangle, Icon, Timer, ArrowRight } from 'lucide-react';
import { soccerBall } from '@lucide/lab';
import styles from './Auth.module.css';

const Auth = () => {
  const location = useLocation();
  const { clubDisplayName, clubLogo } = useTenant();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  // Check if registration is team-based (branch is already known from team)
  const urlParams = new URLSearchParams(location.search);
  const hasTeamId = !!urlParams.get('team') || !!(urlParams.get('redirect')?.includes('team='));

  // Extract club context from URL state or query params
  const getRedirectPath = () => {
    // Check if we came from a club-specific registration URL
    const state = location.state;
    if (state?.from) {
      return state.from;
    }

    // Check for club redirect in URL params
    const urlParams = new URLSearchParams(location.search);
    const redirect = urlParams.get('redirect');
    if (redirect) {
      try {
        const decodedRedirect = decodeURIComponent(redirect);

        // Prevent infinite redirect loops - if decoded redirect is another auth URL, extract the inner redirect
        if (decodedRedirect.startsWith('/auth?redirect=')) {
          const innerParams = new URLSearchParams(decodedRedirect.split('?')[1]);
          const innerRedirect = innerParams.get('redirect');
          if (innerRedirect) {
            return decodeURIComponent(innerRedirect);
          }
        }

        // Convert old tryout URLs to new dedicated routes - DISABLED
        // if (decodedRedirect.includes('/register/') && decodedRedirect.includes('type=tryout')) {
        //   return decodedRedirect.replace('/register/', '/tryouts/register/').replace('?type=tryout', '');
        // }

        return decodedRedirect;
      } catch (e) {
        console.error('Auth: Error decoding redirect:', e);
        return '/register';
      }
    }

    // Default redirect
    return '/register';
  };

  // TRYOUT REGISTRATION STATE - DISABLED
  // const [tryoutRegistration, setTryoutRegistration] = useState({
  //   is_open: false,
  //   season_name: '',
  //   description: ''
  // });
  const { login, registerStep1 } = useAuth();
  const navigate = useNavigate();

  const validationSchema = (values) => {
    const errors = {};

    if (!values.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!values.password) {
      errors.password = 'Password is required';
    } else if (values.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!isLogin && !values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (!isLogin && values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  const { formData, errors, touched, handleFieldChange, handleFieldBlur, handleSubmit: handleValidation, isSubmitting } = useFormValidation({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isLogin) {
          const result = await login(values.email, values.password);
          if (result.success) {
            const redirectPath = getRedirectPath();
            navigate(redirectPath === '/register' ? '/dashboard' : redirectPath);
          } else {
            throw new Error(result.error);
          }
        } else {
          // Extract team parameter from URL if present
          // Check both current URL and redirect parameter
          const urlParams = new URLSearchParams(location.search);
          let teamId = urlParams.get('team');
          let role = urlParams.get('role');

          // If not in current URL, check if it's in the redirect parameter
          if (!teamId) {
            const redirect = urlParams.get('redirect');
            if (redirect) {
              try {
                const decodedRedirect = decodeURIComponent(redirect);
                // Extract query string from redirect URL
                const queryStringStart = decodedRedirect.indexOf('?');
                if (queryStringStart !== -1) {
                  const queryString = decodedRedirect.substring(queryStringStart + 1);
                  const redirectParams = new URLSearchParams(queryString);
                  teamId = redirectParams.get('team');
                  role = redirectParams.get('role');
                }
              } catch (e) {
                console.error('Auth: Error extracting team from redirect:', e);
              }
            }
          }

          const registrationData = {
            email: values.email,
            password: values.password,
            confirm_password: values.confirmPassword
          };

          // Add teamId if present (using camelCase for frontend)
          if (teamId) {
            registrationData.teamId = teamId;
          }

          // Add branchId if selected (NEW: selection-based branch assignment)
          if (selectedBranchId) {
            registrationData.branchId = selectedBranchId;
          }

          // Add role if present
          if (role) {
            registrationData.role = role;
          }

          const result = await registerStep1(registrationData);

          if (result.success) {
            const redirectPath = getRedirectPath();
            navigate(redirectPath);
          } else {
            throw new Error(result.error);
          }
        }
      } catch (error) {
        return { general: error.message || 'An unexpected error occurred' };
      }
    }
  });

  // Initialize form mode based on redirect parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirect = urlParams.get('redirect');

    if (redirect) {
      try {
        const decodedRedirect = decodeURIComponent(redirect);
        // If redirect points to /register, show registration form instead of login
        if (decodedRedirect.startsWith('/register')) {
          setIsLogin(false);
        }
      } catch (e) {
        console.error('Auth: Error parsing redirect for initial form mode:', e);
      }
    }
  }, [location.search]);

  // TRYOUT REGISTRATION CHECK - DISABLED
  // useEffect(() => {
  //   checkTryoutRegistrationStatus();
  // }, []);

  // const checkTryoutRegistrationStatus = async () => {
  //   try {
  //     const result = await tryoutApi.checkRegistrationOpen();
  //     setTryoutRegistration(result);
  //   } catch (error) {
  //     console.error('Error checking tryout registration status:', error);
  //     // Fail silently - non-critical feature
  //   }
  // };

  const handleSubmit = async (e) => {
    handleValidation(e);
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
  };

  return (
    <PageLayout maxWidth="1400px">
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        {clubLogo && (
          <img
            src={clubLogo}
            alt={clubDisplayName}
            style={{
              maxWidth: '200px',
              maxHeight: '80px',
              marginBottom: '1rem',
              objectFit: 'contain'
            }}
          />
        )}
        {clubDisplayName && (
          <h2 style={{
            color: 'var(--club-primary-color, var(--color-primary))',
            marginBottom: '0.5rem',
            fontSize: '1.5rem'
          }}>
            {clubDisplayName}
          </h2>
        )}
      </div>
      <PageHeader
        title={isLogin ? "WELCOME BACK" : "GET STARTED"}
        subtitle={isLogin ? "Sign in to your account" : "Create your parent account"}
      />

      <Section>
        <Card level={1} className={styles.authCard}>
        <form onSubmit={handleSubmit} className={styles.authForm}>
          {errors.general && (
            <div
              role="alert"
              aria-live="assertive"
              className={styles.errorAlert}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <AlertTriangle size={18} className={styles.errorIcon} />
              {errors.general}
            </div>
          )}

          <FormInput
            label="Email Address"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.email ? errors.email : ''}
            required
            placeholder="parent@example.com"
          />

          <FormInput
            label="Password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.password ? errors.password : ''}
            required
            placeholder={isLogin ? "Enter your password" : "Create a strong password"}
          />

          {!isLogin && (
            <>
              <FormInput
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.confirmPassword ? errors.confirmPassword : ''}
                required
                placeholder="Confirm your password"
              />

              {/* Only show branch selector for non-team registrations */}
              {!hasTeamId && (
                <BranchSelector
                  label="Location"
                  value={selectedBranchId}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  required={false}
                  placeholder="Select your location (optional)"
                  helpText="You can choose your location now or later during registration."
                  autoPopulateFromUrl={true}
                />
              )}
            </>
          )}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting ? (isLogin ? 'Signing In...' : 'Creating Account...') : (isLogin ? 'Sign In' : 'Create Account')}
          </Button>

          {isLogin && (
            <div className={styles.forgotPasswordContainer}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className={styles.forgotPasswordButton}
              >
                Forgot your password?
              </button>
            </div>
          )}

          <div className={styles.toggleContainer}>
            <p className={styles.toggleText}>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </p>
            <Button
              variant="outline"
              onClick={toggleMode}
              className={styles.toggleButton}
              disabled={isSubmitting}
            >
              {isLogin ? 'Create Account' : 'Sign In'}
            </Button>
          </div>
        </form>
      </Card>
      </Section>

      {/* Tryout Registration Section - DISABLED */}
      {/* {tryoutRegistration.is_open && (
        <Section>
          <Card level={1} className={styles.tryoutCard}>
          <div className={styles.tryoutContent}>
            <div className={styles.tryoutIcon}><Icon iconNode={soccerBall} size={48} /></div>
            <h3 className={styles.tryoutHeading}>
              {tryoutRegistration.season_name || 'Tryout Registration Open!'}
            </h3>
            <p className={styles.tryoutDescription}>
              {tryoutRegistration.description ||
                'Register your child for tryouts with minimal information. Complete the full registration only after receiving an offer.'}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/tryouts/register')}
              className={styles.tryoutButton}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}
            >
              <Timer size={18} /> Register for Tryouts
            </Button>
          </div>
        </Card>
        </Section>
      )} */}

      {/* Forgot Password Modal */}
      <PasswordResetModal
        isOpen={showForgotPassword}
        onClose={closeForgotPasswordModal}
      />
    </PageLayout>
  );
};

export default Auth;
